import { useCallback, useState } from 'react';
import type { RecutChip } from 'fym-shared/src/music24/musicVideoDoc';
import { recutError, type RecutError } from '../doc/musicVideoErrors';
import { isCutSaved, type CutSaveState } from '../doc/useCutAutosave';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { useRecutMusicVideoCutMutation } from '../musicVideoEditorApi';

/**
 * Starts an AI recut of some sections, or of the whole video (design c6, d3 cut speed). The open
 * cut saves first: the director reads the stored document, so a cut that does not save is not
 * recut (`saveError` holds the save state). The kept clips stay (the server rule).
 *
 * Showcase mode has no director: a recut opens the sign-up card (design c2) and sends nothing.
 */
export function useRecut() {
  const { accountId, catalogItemUuid, cutUuid, flushSave, mode, showUpsell } = useMusicVideoEditor();
  const [recutCut, { isLoading }] = useRecutMusicVideoCutMutation();
  const [error, setError] = useState<RecutError | null>(null);
  const [saveError, setSaveError] = useState<CutSaveState | null>(null);

  const recut = useCallback(
    async (sections: number[] | 'all', chips: RecutChip[], text: string): Promise<boolean> => {
      setError(null);
      setSaveError(null);
      if (mode === 'showcase') {
        showUpsell('recut');
        return false;
      }
      try {
        const saveState = await flushSave();
        if (!isCutSaved(saveState)) {
          setSaveError(saveState);
          return false;
        }
        const trimmed = text.trim();
        await recutCut({
          accountId,
          catalogItemUuid,
          cutUuid,
          sections,
          chips,
          text: trimmed === '' ? undefined : trimmed,
        }).unwrap();
        return true;
      } catch (caught: unknown) {
        console.error('Music video editor: the recut did not start', caught);
        setError(recutError(caught));
        return false;
      }
    },
    [accountId, catalogItemUuid, cutUuid, flushSave, mode, recutCut, showUpsell]
  );

  const clearError = useCallback(() => {
    setError(null);
    setSaveError(null);
  }, []);

  return { recut, isBusy: isLoading, error, saveError, clearError };
}
